<template>
    <div>
        <div class="html-content-wrapper" :class="isMobile ? 'mobile pt-5' : ''">
            <div v-if="loaded && text && text !== 'undefined'">
                <div ref="htmlContent" :class="textClass ? `${textClass}_infotext` : ''" v-html="text"></div>
            </div>

            <div v-else>
                <div class="skeleton mb-4 h-6 rounded"></div>
                <div class="skeleton mb-4 h-6 rounded"></div>
                <div class="skeleton mb-4 h-6 rounded"></div>
                <div class="skeleton mb-4 h-6 rounded"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { getVersionedText } from "~/assets/js/cache";
const { isMobile } = useDevice();
const { $resolveUrl } = useNuxtApp();

const props = defineProps({
    cacheKey: { type: String, required: true },
    textUrl: { type: String, required: true },
    textClass: { type: String }
});

const emit = defineEmits(["textHandler"]);

// Initialize with null or empty string explicitly
const loaded = useState(`${props.cacheKey}Loaded`, () => false);
const text = useState(props.cacheKey, () => "");

const { $getData } = useNuxtApp();
const htmlContent = ref();

// Logic to handle table styling
watch(htmlContent, (newVal) => {
    if (newVal) {
        nextTick(() => {
            const tables = newVal.querySelectorAll("table");
            tables.forEach((el) => {
                if (el && el.parentElement) {
                    el.parentElement.style.width = "100%";
                    el.parentElement.style.overflowX = "auto";
                }
            });
        });
    }
});

onServerPrefetch(async () => {
    try {
        // Temporary until we implement versioning system for server-side
        const urlToFetch = $resolveUrl(props.textUrl);
        const data = await $getData(urlToFetch, props.cacheKey);

        if (data) {
            text.value = data;
        }
    } catch (err) {
        console.error("SSR Fetch Error:", err);
    } finally {
        loaded.value = true;
    }
});

const fetchText = async () => {
    try {
        const res = await getVersionedText(props.textUrl, props.cacheKey);
        if (res && res.status && res.data) {
            text.value = res.data;
        } else {
            emit("textHandler");
        }
    } catch (err) {
        console.error("Client Fetch Error:", err);
        emit("textHandler");
    } finally {
        loaded.value = true;
    }
};

onMounted(() => {
    // If SSR didn't populate the text, fetch on client
    if (!text.value || text.value === "undefined") {
        fetchText();
    } else {
        loaded.value = true;
    }
});
</script>
<style scoped>
.html-content-wrapper :deep(h1) {
    @apply mb-4 text-xl md:text-2xl;
}

.html-content-wrapper :deep(h2) {
    @apply mb-2 mt-4 md:mt-6 md:text-lg;
}

.html-content-wrapper :deep(h3) {
    @apply mb-2 mt-3 md:mt-4;
}

.html-content-wrapper :deep(p),
.html-content-wrapper :deep(li) {
    @apply mb-2 text-xs md:text-sm;
}

.html-content-wrapper :deep(ol),
.html-content-wrapper :deep(ul) {
    padding-left: 1.5rem;
}

.html-content-wrapper :deep(ul) {
    list-style: disc;
}
.html-content-wrapper :deep(ol) {
    list-style: decimal;
}

.html-content-wrapper :deep(ol li),
.html-content-wrapper :deep(ul li) {
    padding-left: 0.5rem;
}

* .html-content-wrapper :deep(table) {
    border-width: 0;
    border-style: solid;
    border-color: inherit;
}

* .html-content-wrapper :deep(tbody),
* .html-content-wrapper :deep(tr),
* .html-content-wrapper :deep(td) {
    border-width: 1px;
    border-style: solid;
    border-color: inherit;
}
</style>
